import type { AppHostInterface, AndroidHostInterface, iOSHostInterface } from "#/app"
import {isAndroidApp, isApp, isIOSApp } from "./app-type-checker.js"

type InferHostReturn<T extends AppHostInterface, C extends keyof T> =
  T extends AndroidHostInterface ? C extends keyof AndroidHostInterface ?
    AndroidHostInterface[C] extends (...args: any[]) => infer F ? F : never : any
    : T extends iOSHostInterface ? Promise<null> : undefined

export function hostAppCall<T extends AndroidHostInterface, C extends keyof Omit<AndroidHostInterface, keyof iOSHostInterface>, P extends string>(command: C, param?: P): AndroidHostInterface[C] extends (args?: any) => infer R ? R | undefined : undefined
export function hostAppCall<T extends iOSHostInterface, C extends keyof Omit<iOSHostInterface, keyof AndroidHostInterface>, P extends string>(command: C, param?: P): Promise<null>
export function hostAppCall<T extends AppHostInterface, C extends keyof T, P extends string>(command: C, param?: P): InferHostReturn<T, C>
export function hostAppCall(command: string, param?: string) {
    if (!isApp()) return
    // NOTE: Since android and iOS app have different ways of sending and receiving the messages with javascript, this function is made.
    // If returned value is needed, then return that value.
    // Parameters(command and param) should be swapped with iOS appVersion < 901.
    try {
        if (isAndroidApp()) return isExistingCommand<AndroidHostInterface>(command) ? handleAndroid(command, param) : undefined
        return isExistingCommand<iOSHostInterface>(command) ? handleIOS(command, param) : undefined
    }
    catch (error) {
        if (import.meta.env.VITE_DEBUG_MODE === "true") console.error(error)
        return
    }
}

const isExistingCommand = <T extends AppHostInterface>(command: any): command is keyof T => {
    if (isAndroidApp()) return command in HostApp!
    if (isIOSApp()) return command
    return false
}

const handleAndroid = <C extends keyof AndroidHostInterface>(command: string & C, param?: string): InferHostReturn<AndroidHostInterface, C> | undefined => {
    if (!HostApp || typeof HostApp[command] !== "function") return
    const fn = HostApp[command]
    // @ts-expect-error
    return typeof param === 'undefined' ? fn.call(HostApp) : fn.call(HostApp, param)
}
const handleIOS = <C extends keyof iOSHostInterface>(command: string & C, param?: string): undefined | Promise<null> => {
    if (!webkit?.messageHandlers?.HostApp) return
    return webkit.messageHandlers.HostApp.postMessage(typeof param === 'undefined' ? command : { command, param })
}

export const backToApp = (): void => {
    hostAppCall("backToApp")
}
// NOTE: If you need asynchronous executions, use asyncHostAppCall instead of hostAppCall, especially to deal with hostAppCall being used too much (e.g, in the list)
// It's better to use new Promise than setTimeout, but 'new Promise(() => hostAppCall)' doesn't work as expected in the current Android app version(493).
export function asyncHostAppCall<T extends UnionToIntersection<AppHostInterface>, C extends keyof T, P extends string>(command: C, param?: P): void {
    setTimeout(() => hostAppCall<T, C, P>(command, param), 10)
}

export const LOG = (text: string) => {
    hostAppCall("debug", text)
    console.log(`debug: ${text}`)
}